import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Info, Loader2, RefreshCw, CheckCircle2, XCircle, Clock, ExternalLink, Search, FileText, Box, Copy } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useStorybookStatus } from '../../context/StorybookStatusContext';

/**
 * Build-details popover showing the Storybook bundle's last exit
 * code, duration, log tail, and a "Rebuild now" / "Open Library"
 * action pair.
 *
 * Status is sourced from the shared StorybookStatusContext, so this
 * component does NOT trigger its own /status polling. While the
 * popover is open it calls `boost()` to switch the shared poll to
 * fast cadence (3s); on close it unboosts back to slow (30s).
 *
 * Optional `trigger` prop lets callers swap the default Info-icon
 * button (e.g. the global header uses a compact status pill).
 */
export default function BuildDetailsPopover({ trigger }) {
  const { status, refresh, boost } = useStorybookStatus();
  const [open, setOpen] = useState(false);
  const [logTail, setLogTail] = useState('');
  const [logLoading, setLogLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState('');

  const buildBaseUrl = useCallback(() => {
    return `${process.env.REACT_APP_BACKEND_URL}/api/admin/storybook/`;
  }, []);

  const openStoryInNewTab = useCallback((entry) => {
    if (!status?.available) {
      toast.error('Component Library is not available yet');
      return;
    }
    const base = buildBaseUrl();
    const url = entry
      ? `${base}?path=/${entry.type === 'story' ? 'story' : 'docs'}/${encodeURIComponent(entry.id)}`
      : base;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [status?.available, buildBaseUrl]);

  const copyDeepLink = useCallback(async (entry) => {
    // Friendly redirect URL — survives bundle rebuilds because it
    // resolves the entry id by title + name on the server.
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/admin/storybook/redirect`;
    const params = new URLSearchParams({ title: entry.title, name: entry.name });
    const url = `${base}?${params.toString()}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Deep link copied', {
        description: `${entry.title} › ${entry.name}`,
      });
    } catch {
      toast.error('Could not copy link to clipboard');
    }
  }, []);

  // Fetch the static index.json once on open so we can power a
  // "jump to story" search box. Uses the same /api/admin/storybook/
  // route — already RBAC-gated.
  const fetchEntries = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/storybook/index.json');
      const list = Object.values(data?.entries || {})
        .filter((e) => e.type === 'story' || e.type === 'docs')
        .map((e) => ({ id: e.id, title: e.title, name: e.name, type: e.type }))
        .sort((a, b) => `${a.title}/${a.name}`.localeCompare(`${b.title}/${b.name}`));
      setEntries(list);
    } catch {
      /* index.json is optional — pill still works without search */
    }
  }, []);

  // Filter the entries by the search box (case-insensitive, fuzzy
  // on the combined "Title — Name" label).
  const filteredEntries = useMemo(() => {
    if (!search.trim()) return entries.slice(0, 6);
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      const label = `${e.title} ${e.name}`.toLowerCase();
      return label.includes(q);
    }).slice(0, 8);
  }, [entries, search]);

  // Fetch the log tail on open. Status itself comes from the context.
  const fetchLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const { data } = await api.get('/admin/storybook/rebuild/log');
      setLogTail(data?.tail || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load build log');
    } finally {
      setLogLoading(false);
    }
  }, []);

  // Boost shared polling while the popover is open + fetch log if allowed.
  useEffect(() => {
    if (!open) return undefined;
    if (status?.can_rebuild) fetchLog();
    fetchEntries();
    refresh();
    const unboost = boost();
    return () => unboost();
  }, [open, status?.can_rebuild, fetchLog, fetchEntries, refresh, boost]);

  // Re-fetch the log whenever a build flips from rebuilding -> done
  // while the popover is open, so the user sees the fresh tail.
  useEffect(() => {
    if (open && status?.can_rebuild && status?.rebuilding === false && status?.last_finished_at) {
      fetchLog();
    }
  }, [open, status?.can_rebuild, status?.rebuilding, status?.last_finished_at, fetchLog]);

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      const { data } = await api.post('/admin/storybook/rebuild');
      if (data.started) {
        toast.success('Rebuild started');
        refresh();
      } else {
        toast.info(`Rebuild not started: ${data.reason}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rebuild failed');
    } finally {
      setRebuilding(false);
    }
  };

  const fmtDuration = () => {
    if (!status?.last_started_at || !status?.last_finished_at) return '—';
    if (status.rebuilding) return 'in progress';
    const ms = (status.last_finished_at - status.last_started_at) * 1000;
    if (ms < 0) return '—';
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const fmtAgo = (ts) => {
    if (!ts) return '—';
    const diff = Date.now() / 1000 - ts;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    return `${Math.round(diff / 3600)}h ago`;
  };

  const renderStatusBadge = () => {
    if (!status) return null;
    if (status.rebuilding) {
      return (
        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
          <Loader2 size={10} className="mr-1 animate-spin" /> Rebuilding…
        </Badge>
      );
    }
    if (status.last_exit_code === 0) {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
          <CheckCircle2 size={10} className="mr-1" /> Healthy
        </Badge>
      );
    }
    if (status.last_exit_code !== null && status.last_exit_code !== 0) {
      return (
        <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">
          <XCircle size={10} className="mr-1" /> Failed
        </Badge>
      );
    }
    if (status.stale) {
      return (
        <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">
          <Clock size={10} className="mr-1" /> Stale
        </Badge>
      );
    }
    return (
      <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Idle</Badge>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-slate-500 hover:text-slate-700"
            title="Component Library build details"
            data-testid="storybook-build-details-trigger"
          >
            <Info size={16} />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[420px] p-0 overflow-hidden"
        data-testid="storybook-build-details-popover"
      >
        <div className="border-b bg-slate-50 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-700">Component Library</span>
            {renderStatusBadge()}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => { refresh(); fetchLog(); }}
            disabled={logLoading}
            title="Refresh"
            data-testid="storybook-build-refresh-btn"
          >
            <RefreshCw size={14} className={logLoading ? 'animate-spin' : ''} />
          </Button>
        </div>

        <div className="px-4 py-3 space-y-3 text-xs">
          <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
            <span className="text-slate-500">Last built</span>
            <span className="font-mono text-slate-700" data-testid="storybook-last-built">
              {fmtAgo(status?.last_finished_at)}
            </span>
            <span className="text-slate-500">Duration</span>
            <span className="font-mono text-slate-700" data-testid="storybook-duration">
              {fmtDuration()}
            </span>
            <span className="text-slate-500">Exit code</span>
            <span className="font-mono text-slate-700" data-testid="storybook-exit-code">
              {status?.last_exit_code ?? '—'}
            </span>
            {status?.can_rebuild && (
              <>
                <span className="text-slate-500">Worker</span>
                <span className="font-mono text-slate-700 truncate" title={status?.worker_id || ''}>
                  {status?.worker_id || '—'}
                </span>
              </>
            )}
            <span className="text-slate-500">Sources stale?</span>
            <span className="font-mono text-slate-700">
              {status?.stale ? 'yes' : 'no'}
            </span>
          </div>

          {/* Jump-to-story search */}
          <div className="space-y-1.5" data-testid="storybook-jump-section">
            <div className="text-slate-500">Jump to a story</div>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={entries.length ? `Search ${entries.length} entries…` : 'Loading entries…'}
                className="h-8 pl-7 text-xs"
                data-testid="storybook-jump-input"
              />
            </div>
            {(search || entries.length > 0) && (
              <div className="border border-slate-200 rounded-md overflow-hidden divide-y divide-slate-100 max-h-40 overflow-y-auto">
                {filteredEntries.length === 0 ? (
                  <div className="px-2 py-2 text-slate-400 text-[11px]">No matches.</div>
                ) : (
                  filteredEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className="group flex items-center gap-1 px-2 py-1.5 hover:bg-slate-50 transition-colors text-[11px]"
                      data-testid={`storybook-jump-row-${entry.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => openStoryInNewTab(entry)}
                        className="flex-1 text-left flex items-center gap-2 min-w-0"
                        data-testid={`storybook-jump-item-${entry.id}`}
                        title={`Open ${entry.title} → ${entry.name}`}
                      >
                        {entry.type === 'docs' ? (
                          <FileText size={11} className="text-blue-500 shrink-0" />
                        ) : (
                          <Box size={11} className="text-emerald-500 shrink-0" />
                        )}
                        <span className="font-medium text-slate-700 truncate">{entry.title}</span>
                        <span className="text-slate-400">›</span>
                        <span className="text-slate-600 truncate">{entry.name}</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); copyDeepLink(entry); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity rounded p-1 hover:bg-slate-200"
                        data-testid={`storybook-copy-link-${entry.id}`}
                        title="Copy share-friendly deep link"
                      >
                        <Copy size={11} className="text-slate-500" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {status?.can_rebuild && (
            <div>
              <div className="text-slate-500 mb-1">Log tail</div>
              <pre
                className="bg-slate-900 text-slate-100 rounded-md p-2 max-h-48 overflow-auto text-[10px] leading-tight font-mono whitespace-pre-wrap"
                data-testid="storybook-log-tail"
              >
                {logTail || 'No build logs yet.'}
              </pre>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <Button
              size="sm"
              variant="default"
              onClick={() => openStoryInNewTab(null)}
              disabled={!status?.available}
              data-testid="storybook-open-from-popover-btn"
            >
              <ExternalLink size={14} className="mr-1.5" />
              Open Library
            </Button>
            {status?.can_rebuild && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleRebuild}
                disabled={rebuilding || status?.rebuilding}
                data-testid="storybook-rebuild-now-btn"
              >
                {(rebuilding || status?.rebuilding) ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw size={14} className="mr-1.5" />
                )}
                Rebuild now
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
